// src/components/InstallBanner.js
// Bandeau d'installation PWA
// – Sur Android/Chrome : affiche le prompt natif
// – Sur iOS/Safari : affiche les instructions manuelles

import React, { useState, useEffect } from 'react';
import { usePWA } from '../hooks/usePWA';

export default function InstallBanner() {
  const { installPrompt, isInstalled, promptInstall } = usePWA();
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [dismissed,    setDismissed]    = useState(false);

  // Détecter iOS
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isIOSSafari = isIOS && isSafari;

  useEffect(() => {
    // Ne pas afficher si déjà installée ou si l'utilisateur a refusé cette session
    const dismissed = sessionStorage.getItem('kindeo_install_dismissed');
    if (dismissed) setDismissed(true);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem('kindeo_install_dismissed', '1');
    setDismissed(true);
    setShowIOSGuide(false);
  };

  // Ne rien afficher si : déjà installée, déjà refusé, ou pas de prompt disponible
  if (isInstalled || dismissed) return null;
  if (!installPrompt && !isIOSSafari) return null;

  return (
    <>
      {/* ── BANDEAU PRINCIPAL ─────────────────────────── */}
      <div style={s.banner}>
        <div style={s.bannerLeft}>
          <div style={s.appIcon}>K</div>
          <div>
            <div style={s.bannerTitle}>Installer Kindeo</div>
            <div style={s.bannerSub}>Accès rapide depuis votre écran d'accueil</div>
          </div>
        </div>
        <div style={s.bannerActions}>
          {installPrompt ? (
            <button style={s.installBtn} onClick={promptInstall}>
              Installer
            </button>
          ) : isIOSSafari ? (
            <button style={s.installBtn} onClick={() => setShowIOSGuide(true)}>
              Comment ?
            </button>
          ) : null}
          <button style={s.dismissBtn} onClick={dismiss} aria-label="Fermer">✕</button>
        </div>
      </div>

      {/* ── GUIDE IOS ─────────────────────────────────── */}
      {showIOSGuide && (
        <div style={s.overlay} onClick={() => setShowIOSGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalTitle}>Ajouter à l'écran d'accueil</div>
            <div style={s.step}>
              <div style={s.stepNum}>1</div>
              <div style={s.stepText}>
                Appuyez sur <strong>Partager</strong>{' '}
                <span style={{ fontSize: 18 }}>⎙</span> en bas de Safari
              </div>
            </div>
            <div style={s.step}>
              <div style={s.stepNum}>2</div>
              <div style={s.stepText}>
                Faites défiler et appuyez sur{' '}
                <strong>« Sur l'écran d'accueil »</strong>
              </div>
            </div>
            <div style={s.step}>
              <div style={s.stepNum}>3</div>
              <div style={s.stepText}>
                Appuyez sur <strong>Ajouter</strong> en haut à droite
              </div>
            </div>
            <button style={s.modalClose} onClick={() => setShowIOSGuide(false)}>
              Compris
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const s = {
  banner:        { position: 'fixed', bottom: 'calc(var(--botnav-h, 68px) + 10px)', left: 12, right: 12, background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 'var(--radius-lg)', padding: '12px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: 'var(--shadow-lg)', zIndex: 200 },
  bannerLeft:    { display: 'flex', alignItems: 'center', gap: 12 },
  appIcon:       { width: 42, height: 42, borderRadius: 10, background: 'var(--c-primary)', color: '#fff', fontFamily: 'Manrope,sans-serif', fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  bannerTitle:   { fontFamily: 'Manrope,sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--c-text)' },
  bannerSub:     { fontFamily: 'Manrope,sans-serif', fontSize: 11, color: 'var(--c-text-3)', marginTop: 2 },
  bannerActions: { display: 'flex', alignItems: 'center', gap: 8 },
  installBtn:    { background: 'var(--c-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)', padding: '7px 14px', fontFamily: 'Manrope,sans-serif', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  dismissBtn:    { background: 'none', border: 'none', color: 'var(--c-text-3)', fontSize: 16, cursor: 'pointer', padding: '4px 6px' },
  overlay:       { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 300, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' },
  modal:         { background: 'var(--c-surface)', borderRadius: '24px 24px 0 0', padding: '28px 24px 40px', width: '100%', maxWidth: 480 },
  modalTitle:    { fontFamily: 'Manrope,sans-serif', fontSize: 18, fontWeight: 800, color: 'var(--c-primary)', marginBottom: 24, textAlign: 'center' },
  step:          { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 18 },
  stepNum:       { width: 28, height: 28, borderRadius: '50%', background: 'var(--c-primary)', color: '#fff', fontFamily: 'Manrope,sans-serif', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  stepText:      { fontSize: 15, color: 'var(--c-text-2)', lineHeight: 1.5, paddingTop: 3 },
  modalClose:    { width: '100%', padding: 14, background: 'var(--c-primary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-pill)', fontFamily: 'Manrope,sans-serif', fontSize: 15, fontWeight: 700, cursor: 'pointer', marginTop: 8 },
};
